<!-- src/App.vue -->
<template>
  <div class="app-container min-h-screen bg-gray-900">
    <SidebarMenu />

    <div
      class="content-container transition-all duration-300 flex flex-col"
      :class="[isSidebarOpen ? 'with-sidebar-open' : 'with-sidebar-closed']"
    >
      <!-- Navigation -->
      <nav
        class="bg-gray-800 shadow-lg border-b border-gray-700 w-full relative z-50"
      >
        <!-- Responsive container -->
        <div class="w-full max-w-full px-3 sm:px-4 md:px-6 lg:px-8">
          <div class="flex items-center justify-between h-14 sm:h-16">
            <div
              class="flex items-center space-x-2 sm:space-x-4 md:space-x-8 flex-1 min-w-0"
            >
              <!-- Logo/Home -->
              <router-link
                to="/"
                class="text-white font-bold text-lg sm:text-xl hover:text-indigo-400 transition-colors flex-shrink-0 flex items-center space-x-2"
              >
                <img src="@/assets/logo.png" alt="Logo" class="h-12 sm:h-12" />
                <span>Moja Garderoba</span>
              </router-link>

              <!-- Navigation Links - Hidden on mobile, shown on tablet+ -->
              <div class="hidden sm:flex items-center space-x-2 md:space-x-4">
                <router-link
                  to="/products"
                  class="text-gray-300 hover:text-white px-2 md:px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap"
                >
                  Proizvodi
                </router-link>

                <!-- Add new listing link - Only when logged in -->
                <router-link
                  v-if="isLoggedIn"
                  to="/create-product"
                  class="bg-indigo-600 hover:bg-indigo-700 text-white px-2 md:px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors flex items-center space-x-1 md:space-x-2 whitespace-nowrap"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <span>Dodaj novi oglas</span>
                </router-link>
              </div>
            </div>

            <!-- Auth Links -->
            <div
              class="flex items-center space-x-1 sm:space-x-2 md:space-x-4 flex-shrink-0"
            >
              <template v-if="!isLoggedIn">
                <router-link
                  to="/login"
                  class="text-gray-300 hover:text-white px-2 sm:px-3 py-1 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap"
                  :class="{ 'bg-gray-900': $route.path === '/login' }"
                >
                  Prijava
                </router-link>
                <router-link
                  to="/register"
                  class="bg-indigo-600 hover:bg-indigo-700 text-white px-2 sm:px-4 py-1 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap"
                >
                  Registracija
                </router-link>
              </template>
              <template v-else>
                <div class="flex items-center space-x-1 sm:space-x-2">
                  <!-- Username - Hidden on very small screens -->
                  <div
                    v-if="currentUser"
                    class="hidden md:block text-gray-300 text-sm truncate max-w-[120px]"
                  >
                    {{ currentUser.username || currentUser.email }}
                  </div>
                  <router-link
                    v-if="currentUser"
                    :to="`/profile/${currentUser.id}`"
                    class="text-gray-300 hover:text-white px-2 sm:px-3 py-1 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap"
                    :class="{ 'bg-gray-900': $route.path === '/profile' }"
                  >
                    Profil
                  </router-link>
                  <button
                    @click="logout"
                    class="text-gray-300 hover:text-white px-2 sm:px-3 py-1 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap"
                  >
                    Odjava
                  </button>
                </div>
              </template>
            </div>
          </div>

          <!-- Mobile Navigation Links - Shown only on mobile -->
          <div class="sm:hidden border-t border-gray-700 py-2">
            <div class="flex items-center justify-between space-x-2 px-3">
              <router-link
                to="/products"
                class="text-gray-300 hover:text-white py-2 rounded-md text-sm font-medium transition-colors flex-1"
              >
                Proizvodi
              </router-link>

              <!-- Mobile Add new listing button - Compact version -->
              <router-link
                v-if="isLoggedIn"
                to="/create-product"
                class="bg-indigo-600 hover:bg-indigo-700 text-white px-2 py-2 rounded-md text-xs font-medium transition-colors flex items-center space-x-1 flex-shrink-0 whitespace-nowrap"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span>Dodaj novi oglas</span>
              </router-link>
            </div>
          </div>
        </div>
      </nav>

      <!-- Main Content -->
      <main class="flex-grow py-6">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <router-view v-if="isInitialized" />
          <div v-else class="flex justify-center items-center min-h-[50vh]">
            <div class="text-white">Učitavanje...</div>
          </div>
        </div>
      </main>

      <!-- Footer -->
      <AppFooter class="relative z-50" />
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  computed,
  onMounted,
  onUnmounted,
  ref,
  watch,
  nextTick,
} from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useNotifications } from '@/composables/useNotifications';
import SidebarMenu from '@/components/SidebarMenu.vue';
import AppFooter from '@/components/Footer.vue';
import axios from 'axios';

export default defineComponent({
  name: 'App',
  components: {
    SidebarMenu,
    AppFooter,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const isSidebarOpen = ref(true);
    const isInitialized = ref(false);

    // Use notifications composable
    const { initializeNotifications, cleanupNotifications } =
      useNotifications();

    // Computed properties
    const isLoggedIn = computed(() => store.state.auth.isLoggedIn);
    const currentUser = computed(() => store.getters['auth/currentUser']);

    const loadUserData = async () => {
      // Učitaj podatke o korisniku ako postoji token
      const token = localStorage.getItem('token');
      if (token) {
        try {
          console.log(
            '[App] Loading user data with token:',
            token.substring(0, 20) + '...'
          );
          store.commit('auth/SET_LOADING', true);

          // Pozivamo fetchCurrentUser i čekamo rezultat
          await store.dispatch('auth/fetchCurrentUser');

          // Postavimo stanje prijave
          store.commit('auth/SET_LOGGED_IN', true);

          console.log('[App] User data loaded successfully');

          // ISPRAVKA: Inicijalizuj notifikacije odmah nakon uspešnog učitavanja korisnika
          await nextTick(); // Sačekaj da se DOM ažurira
          await initializeNotifications();
          console.log('[App] Notifications initialized after login');
        } catch (error) {
          console.error('[App] Error loading user data:', error);
          // Ako je 401 Unauthorized ili druga greška autentifikacije, odjavimo korisnika
          if (axios.isAxiosError(error) && error.response?.status === 401) {
            console.log('[App] Token invalid, logging out');
            store.dispatch('auth/logout');
            cleanupNotifications();
          }
        } finally {
          store.commit('auth/SET_LOADING', false);
        }
      } else {
        console.log('[App] No token found');
      }

      // Postaviti inicijalizaciju na završeno
      isInitialized.value = true;
    };

    // Watch for authentication changes
    watch(isLoggedIn, async (newValue, oldValue) => {
      console.log('[App] Auth state changed:', {
        from: oldValue,
        to: newValue,
      });

      if (newValue && !oldValue) {
        // User just logged in - initialize notifications
        console.log('[App] User logged in, initializing notifications');
        await nextTick();
        await initializeNotifications();
      } else if (!newValue && oldValue) {
        // User just logged out - cleanup notifications
        console.log('[App] User logged out, cleaning up notifications');
        cleanupNotifications();
      }
    });

    // Load saved sidebar state
    onMounted(async () => {
      console.log('[App] Component mounted, loading user data');

      // Učitaj stanje sidebar-a iz localStorage-a
      const savedState = localStorage.getItem('sidebarOpen');
      if (savedState !== null) {
        isSidebarOpen.value = savedState === 'true';
      }

      // Učitaj korisničke podatke
      await loadUserData();

      // Dodaj event listener za promene stanja sidebar-a
      const handleSidebarChange = (event: CustomEvent) => {
        if (event.detail && typeof event.detail.isOpen === 'boolean') {
          isSidebarOpen.value = event.detail.isOpen;
        }
      };

      document.addEventListener(
        'sidebarStateChanged',
        handleSidebarChange as EventListener
      );

      // Oslobađanje event listenera kada se komponenta uništi
      onUnmounted(() => {
        document.removeEventListener(
          'sidebarStateChanged',
          handleSidebarChange as EventListener
        );

        // Cleanup notifications on unmount
        cleanupNotifications();
      });
    });

    const logout = async () => {
      console.log('[App] Logging out');
      // Cleanup notifications before logout
      cleanupNotifications();

      await store.dispatch('auth/logout');
      router.push('/login');
    };

    return {
      isSidebarOpen,
      isLoggedIn,
      currentUser,
      logout,
      isInitialized,
    };
  },
});
</script>

<style>
body {
  margin: 0;
  padding: 0;
}

#app {
  font-family: system-ui, -apple-system, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.app-container {
  display: flex;
  width: 100%;
}

.content-container {
  flex: 1;
  min-height: 100vh;
  transition: margin-left 0.3s ease;
}

/* Desktop sidebar states - content shifts right but header/footer stay full width */
@media (min-width: 768px) {
  .with-sidebar-open {
    margin-left: 0; /* Remove margin, content will be covered partially */
  }

  .with-sidebar-closed {
    margin-left: 0; /* Remove margin, content will be covered partially */
  }

  /* Only main content gets margin to avoid sidebar overlap while keeping content centered */
  .with-sidebar-open main {
    margin-left: 16rem; /* 256px */
    margin-right: 0;
  }

  .with-sidebar-closed main {
    margin-left: 4rem; /* 64px */
    margin-right: 0;
  }
}

/* Mobile - sidebar and content side by side */
@media (max-width: 767px) {
  .with-sidebar-open,
  .with-sidebar-closed {
    margin-left: 0;
  }

  /* Mobile sidebar positioning - side by side with content */
  .with-sidebar-open main {
    margin-left: 16rem; /* Same as desktop when open */
  }

  .with-sidebar-closed main {
    margin-left: 4rem; /* Same as desktop when closed */
  }
}

/* Navigation and footer layering */
nav {
  width: 100%;
  position: relative;
  z-index: 50; /* Above sidebar */
}

/* Ensure sidebar has lower z-index */
.app-container {
  position: relative;
}

/* Main content transition for sidebar */
main {
  transition: margin-left 0.3s ease;
}

/* Remove the max-width override for better responsive behavior */
nav > div {
  max-width: 100%;
}

/* Ensure content doesn't overflow on small screens */
@media (max-width: 640px) {
  .content-container {
    min-width: 0;
  }

  nav .flex {
    flex-wrap: nowrap;
  }

  /* Adjust main content container for very small screens */
  .with-sidebar-open main > div {
    max-width: none;
    padding-left: 2rem;
    padding-right: 1rem;
  }

  .with-sidebar-closed main > div {
    max-width: none;
    padding-left: 1rem;
    padding-right: 1rem;
  }
}

/* Global transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
